import React, { useEffect, useState, useCallback } from "react";
import MUIDataTable from "mui-datatables";
import {
  Button,
  IconButton,
  Tooltip,
  DialogContent,
  Dialog,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  Close,
  DeleteOutlineOutlined,
  RemoveRedEyeOutlined,
  EditOutlined,
} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import { Helmet } from "react-helmet";
import { CSVLink } from "react-csv";
import "react-quill/dist/quill.snow.css";
import { useAuth } from "../auth/authContext";
import { useLocation } from "react-router-dom";
import Axios from "../axios";

const Subscription = () => {
  const { navData } = useAuth();
  const location = useLocation();

  const [subscriptionState, setSubscriptionState] = useState({
    open: false,
    opendel: false,
    planName: "",
    display: "",
    duration: "",
    description: "",
    price: "",
    userId: "",
    type: "",
    status: "active",
    page: 0,
    count: 0,
    search: "",
    rankPlace: "",
    write: false,
  });

  const [perks, setPerks] = useState([{ description: "", status: true }]);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [downloadData, setDownloadData] = useState([]);
  const {
    open,
    opendel,
    planName,
    display,
    duration,
    description,
    price,
    userId,
    type,
    status,
    page,
    count,
    search,
    rankPlace,
    write,
  } = subscriptionState;

  const updateState = (updates) => {
    setSubscriptionState((prev) => ({ ...prev, ...updates }));
  };

  const headers = [
    { label: "Plan Name", key: "planName" },
    { label: "Description", key: "description" },
    { label: "Price", key: "price" },
    { label: "Duration", key: "duration" },
    { label: "Plan Status", key: "status" },
    { label: "Perk Description", key: "description" },
    { label: "Perk Status", key: "status" },
  ];

  const fetchAllSubscriptions = useCallback((page = 0, searchText = "") => {
    const data = { page, searchChar: searchText };

    Axios.post(`${process.env.REACT_APP_BACKEND}get-all-subscription`, data)
      .then((resp) => {
        const responseData = resp?.data?.data;
        if (responseData) {
          setAllSubscriptions(responseData.docs || []);
          updateState({ count: responseData.totalDocs || 0 });
        }
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.message || "Error fetching subscriptions";
        toast.error(errorMsg);
      });
  }, []);

  const handleDownloadProcess = useCallback(() => {
    Axios.post(`${process.env.REACT_APP_BACKEND}get-subscription-csv-data`)
      .then((resp) => {
        setDownloadData(resp?.data?.data || []);
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.message || "Error fetching download data";
        toast.error(errorMsg);
      });
  }, []);

  useEffect(() => {
    fetchAllSubscriptions(0, "");
    handleDownloadProcess();
  }, [fetchAllSubscriptions, handleDownloadProcess]);

  useEffect(() => {
    const currentPath = location.pathname;
    const matchingNavItem = navData.find((item) => item.link === currentPath);
    updateState({ write: matchingNavItem?.write || false });
  }, [location.pathname, navData]);

  // Perks management
  const addPerkField = () => {
    setPerks([...perks, { description: "", status: true }]);
  };

  const removePerkField = (index) => {
    setPerks(perks.filter((_, i) => i !== index));
  };

  const handlePerkChange = (index, field, value) => {
    const updatedPerks = [...perks];
    updatedPerks[index][field] = value;
    setPerks(updatedPerks);
  };

  const handleOpen = (userData, actionType, statusType) => {
    if (actionType === "delete") {
      updateState({
        userId: userData,
        opendel: true,
        type: "delete",
      });
    } else if (actionType === "block") {
      updateState({
        userId: userData,
        opendel: true,
        status: statusType,
        type: "block",
      });
    } else if (actionType === "edit" || actionType === "view") {
      setPerks(userData?.perks || [{ description: "", status: true }]);
      updateState({
        open: true,
        planName: userData?.planName || "",
        display: userData?.displayName || "",
        description: userData?.description || "",
        price: userData?.price || "",
        duration: userData?.duration || "",
        userId: userData?._id || "",
        status: userData?.status || "active",
        rankPlace: userData?.rankPlace || "",
        type: actionType,
      });
    } else {
      updateState({ open: true });
    }
  };

  const handleClose = () => {
    updateState({
      open: false,
      opendel: false,
      type: "",
      planName: "",
      display: "",
      description: "",
      userId: "",
      price: "",
      duration: "",
      status: "active",
      rankPlace: "",
    });
    setPerks([{ description: "", status: true }]);
  };

  // Input handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "name":
        updateState({ planName: value });
        break;
      case "display":
        updateState({ display: value });
        break;
      case "description":
        updateState({ description: value });
        break;
      case "price":
        updateState({ price: value });
        break;
      case "duration":
        updateState({ duration: value });
        break;
      case "status":
        updateState({ status: value });
        break;
      default:
        break;
    }
  };

  const handleSubmit = () => {
    if (!planName?.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!description?.trim()) {
      toast.error("Description is required");
      return;
    }

    const isEdit = type === "edit";
    const api = isEdit ? "update-subscription" : "create-subscription";

    const data = {
      planName,
      displayName: display,
      description,
      price,
      duration,
      status,
      rankPlace,
      perks,
      ...(isEdit && { id: userId }),
    };

    Axios.post(`${process.env.REACT_APP_BACKEND}${api}`, data)
      .then((resp) => {
        if (resp?.data?.data?.docs) {
          setAllSubscriptions(resp.data.data.docs);
          updateState({ count: resp.data.data.totalDocs });
        }
        toast.success(resp?.data?.message || "Operation successful");
        handleClose();
      })
      .catch((err) => {
        console.error(err);
        toast.error(err?.message || "An error occurred");
      });
  };

  // Delete handler
  const handleDelete = () => {
    const api =
      type === "block" ? "update-subscription-status" : "delete-subscription";

    Axios.post(`${process.env.REACT_APP_BACKEND}${api}`, { id: userId })
      .then((resp) => {
        if (resp?.data?.data?.docs) {
          setAllSubscriptions(resp.data.data.docs);
          updateState({ count: resp.data.data.totalDocs });
        }
        toast.success(resp?.data?.message || "Operation successful");
        handleClose();
      })
      .catch((err) => {
        const errorMsg =
          err?.resp?.errors?.errors?.message || "Error processing request";
        toast.error(errorMsg);
      });
  };

  const options = {
    responsive: "standard",
    selectableRows: "none",
    filter: false,
    rowsPerPageOptions: [],
    viewColumns: false,
    serverSide: true,
    count,
    page,
    download: false,
    onTableChange: (action, tableState) => {
      if (action === "changePage") {
        fetchAllSubscriptions(tableState.page, search);
        updateState({ page: tableState.page });
      }
    },
    onSearchChange: (searchText) => {
      fetchAllSubscriptions(page, searchText);
      updateState({ search: searchText });
    },
    customToolbar: () => (
      <Tooltip title="Download">
        <IconButton>
          <CSVLink
            data={downloadData}
            headers={headers}
            filename={"Subscription.csv"}
          >
            <DownloadIcon />
          </CSVLink>
        </IconButton>
      </Tooltip>
    ),
  };

  const columns = [
    {
      name: "planName",
      label: "Plan Name",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "rankPlace",
      label: "Rank Place",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "price",
      label: "Price",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "duration",
      label: "Duration",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => (
          <p>
            {allSubscriptions[dataIndex]?.duration === "yearly"
              ? "Year"
              : "Month"}
          </p>
        ),
      },
    },
    {
      name: "status",
      label: "Plan Status",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const isActive = allSubscriptions[dataIndex]?.status === "active";
          return (
            <p className={`capsule ${isActive ? "published" : "unpublished"}`}>
              {isActive ? "Active" : "Inactive"}
            </p>
          );
        },
      },
    },
    {
      name: "Action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const subscription = allSubscriptions[dataIndex];
          return (
            <>
              <Tooltip title="View">
                <IconButton
                  aria-label="View Subscription"
                  onClick={() => handleOpen(subscription, "view")}
                >
                  <RemoveRedEyeOutlined className="icon-size" />
                </IconButton>
              </Tooltip>

              {write && (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      aria-label="Edit Subscription"
                      onClick={() => handleOpen(subscription, "edit")}
                    >
                      <EditOutlined className="icon-size" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      aria-label="Delete Subscription"
                      onClick={() => handleOpen(subscription._id, "delete")}
                    >
                      <DeleteOutlineOutlined className="red-color fs-16 icon-size" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </>
          );
        },
      },
    },
  ];

  const renderViewPerks = () => (
    <div className="perks-list">
      <h4 className="view-style fs-20 fw-500 text-center">Perks:</h4>
      <div className="d-flex justify-content-between">
        <p className="view-style fs-16 fw-500">Description</p>
        <p className="view-style fs-16 fw-500">Status</p>
      </div>
      {perks.map((perk, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <p className="view-style fs-16">{perk.description}</p>
          <p
            className="view-style fs-16"
            style={perk.status ? { color: "#22ccb2" } : { color: "red" }}
          >
            {perk.status ? "Active" : "Inactive"}
          </p>
        </div>
      ))}
    </div>
  );

  const renderEditPerks = () => (
    <div>
      <p className="mb-3 mt-3" style={{ fontSize: "14px" }}>
        Perks
      </p>
      {perks.map((perk, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div style={{ flex: 2 }}>
            <TextField
              label="Perks Description"
              name={`description-${index}`}
              variant="outlined"
              required
              fullWidth
              className="my-2"
              onChange={(e) =>
                handlePerkChange(index, "description", e.target.value)
              }
              value={perk.description}
              InputProps={{ readOnly: type === "view" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <FormControl fullWidth className="my-2" required>
              <InputLabel id={`status-label-${index}`}>Perks Status</InputLabel>
              <Select
                labelId={`status-label-${index}`}
                name={`status-${index}`}
                value={perk.status}
                onChange={(e) =>
                  handlePerkChange(index, "status", e.target.value)
                }
                disabled={type === "view"}
                label="Perks Status"
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
          {type !== "view" && (
            <Button
              className="cancel-button"
              onClick={() => removePerkField(index)}
              variant="contained"
              disabled={perks.length === 1}
            >
              Delete
            </Button>
          )}
        </div>
      ))}
      {type !== "view" && (
        <Button
          onClick={addPerkField}
          variant="contained"
          style={{ marginLeft: "8px" }}
        >
          Add
        </Button>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Sevenly-Subscription</title>
        <meta name="FAQ" content="FAQ for Home Page" />
      </Helmet>

      <h1 className="mb--12 sevenly-cursor"> — </h1>
      <div className="d-flex justify-content-between mb-2">
        <h2>Subscription Management</h2>
        {write && (
          <Button
            className="w7 pd sevenly-btn text-white"
            onClick={() => handleOpen(null, null)}
          >
            Add
          </Button>
        )}
      </div>
      <MUIDataTable
        title={"Subscription List"}
        data={allSubscriptions}
        columns={columns}
        options={options}
      />
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        fullWidth={true}
        open={open}
        maxWidth="md"
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>
            {type === "edit"
              ? "Edit Subscription Details"
              : type === "view"
              ? "View Subscription"
              : "Create New Subscription"}
          </h3>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>

        <DialogContent dividers>
          {type === "view" ? (
            <div className="viewDetails">
              <p className="view-style fs-20 fw-500 ">
                Plan Name:
                <span className="view-style fs-20 fw-400 ms-2">{planName}</span>
              </p>
              <p className="view-style fs-20 fw-500">
                Plan Description:{" "}
                <span className="view-style fs-20 fw-400 ms-2">
                  {description}
                </span>
              </p>
              <p className="view-style fs-20 fw-500">
                Price:{" "}
                <span className="view-style fs-20 fw-400 ms-2">{price}</span>
              </p>
              <p className="view-style fs-20 fw-500">
                Rank Place:{" "}
                <span className="view-style fs-20 fw-400 ms-2">
                  {rankPlace}
                </span>
              </p>
              {renderViewPerks()}
            </div>
          ) : (
            <>
              <TextField
                label="Plan Name"
                variant="outlined"
                required
                fullWidth
                className="my-2"
                name="name"
                onChange={handleChange}
                value={planName}
                InputProps={{ readOnly: type === "view" }}
              />
              <TextField
                label="Display Name"
                variant="outlined"
                required
                fullWidth
                className="my-2 mt-4"
                name="display"
                onChange={handleChange}
                value={display}
                InputProps={{ readOnly: type === "view" }}
              />
              <TextField
                label="Description"
                variant="outlined"
                required
                fullWidth
                className="my-2 mt-4"
                name="description"
                onChange={handleChange}
                value={description}
                InputProps={{ readOnly: type === "view" }}
              />
              <TextField
                label="Rank Place"
                variant="outlined"
                required
                type="number"
                fullWidth
                className="my-2 mt-4"
                name="rankPlace"
                onChange={(e) => updateState({ rankPlace: e.target.value })}
                value={rankPlace}
                InputProps={{ readOnly: type === "view" }}
              />
              <TextField
                label="Price"
                variant="outlined"
                required
                fullWidth
                className="my-2 mt-4"
                name="price"
                onChange={handleChange}
                value={price}
                InputProps={{ readOnly: type === "view" }}
              />
              <FormControl
                fullWidth
                variant="outlined"
                className="my-2 mt-4"
                required
              >
                <InputLabel id="duration-label">Duration</InputLabel>
                <Select
                  labelId="plan-status-label"
                  id="duration"
                  name="duration"
                  value={duration}
                  onChange={handleChange}
                  disabled={type === "view"}
                  label="Plan Status"
                >
                  <MenuItem value="monthly">Month</MenuItem>
                  <MenuItem value="yearly">Year</MenuItem>
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                variant="outlined"
                className="my-2 mt-4"
                required
              >
                <InputLabel id="plan-status-label">Plan Status</InputLabel>
                <Select
                  labelId="plan-status-label"
                  id="plan-status"
                  name="status"
                  value={status}
                  onChange={handleChange}
                  disabled={type === "view"}
                  label="Plan Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              {renderEditPerks()}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button className="cancel-button" onClick={handleClose}>
            Cancel
          </Button>
          {type !== "view" && (
            <Button
              className="submit-button"
              variant="contained"
              onClick={handleSubmit}
            >
              {type === "edit" ? "Submit" : "Create"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        fullWidth={true}
        open={opendel}
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>
            {type === "block" ? (status ? "Unpublish" : "Publish") : "Delete"}
          </h3>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>

        <DialogContent dividers>
          <p className="fs-14">
            {type === "block"
              ? status
                ? "Are you sure you want to unpublish the subscription?"
                : "Are you sure you want to publish the subscription?"
              : "Are you sure you want to delete the subscription?"}
          </p>
        </DialogContent>

        <DialogActions>
          <Button className="cancel-button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="submit-button"
            variant="contained"
            onClick={handleDelete}
          >
            {type === "block" ? (status ? "Unpublish" : "Publish") : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Subscription;
